import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { BRAND } from '@/constants'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './Voucher.module.css'

interface VoucherBaseProps {
  amount: number
  expirationDate?: string
  ariaLabel?: string
}

interface StaticVoucherProps extends VoucherBaseProps {
  mode?: 'static'
  href?: never
  onClick?: never
}

interface LinkVoucherProps extends VoucherBaseProps {
  mode: 'link'
  href: string
  onClick?: never
}

interface ButtonVoucherProps extends VoucherBaseProps {
  mode: 'button'
  href?: never
  onClick: () => void
}

type VoucherProps = StaticVoucherProps | LinkVoucherProps | ButtonVoucherProps

export function Voucher({ mode, amount, ariaLabel, expirationDate, href, onClick }: VoucherProps) {
  const intl = useIntl()
  const isInteractive = mode === 'link' || mode === 'button'
  const brandName = useConfiguration('brandName')

  const iconGlyph = brandName === BRAND.ELIZA ? 'LogoElizaWasHere' : 'LogoSunweb'

  const content = (
    <>
      <div className={styles.iconContainer} aria-hidden>
        <Icon glyph={iconGlyph} size={50} />
      </div>
      <div className={styles.content}>
        <div className={styles.textContent}>
          <Typography variant="display-f-regular">{intl.formatCurrency(amount)}</Typography>
          {expirationDate && (
            <Typography variant="body-s-bold-tight" className={styles.date}>
              <Icon glyph="Calendar" size={20} />
              {intl.formatMessage({ id: 'common_voucher_expiration' }, { date: expirationDate })}
            </Typography>
          )}
        </div>
        {mode === 'link' && <Icon glyph="ChevronRight" size={24} />}
      </div>
    </>
  )

  return (
    <div
      className={cn(styles.cardWrapper, isInteractive && styles.interactiveCardWrapper)}
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {mode === 'link' && (
        <a className={cn(styles.card, styles.interactiveCard)} href={href}>
          {content}
        </a>
      )}
      {mode === 'button' && (
        <button
          type="button"
          className={cn(styles.card, styles.interactiveCard, styles.buttonCard)}
          onClick={onClick}
        >
          {content}
        </button>
      )}
      {(!mode || mode === 'static') && <div className={styles.card}>{content}</div>}
    </div>
  )
}
